import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Layers, Save, Image as ImageIcon, Palette } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { circuitsAPI, handleApiError } from '../../utils/api';

const EMPTY_FORM = {
  title: '',
  acronym: '',
  organizer: '',
  logoUrl: '',
  color: 'bg-blue-600',
};

const CreateEditCircuitPage = () => {
  const navigate = useNavigate();
  const { circuitId } = useParams();
  const { userRole } = useAuth();
  const isEditMode = !!circuitId;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadCircuit = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await circuitsAPI.getById(circuitId);
      const circuit = response.data;
      setFormData({
        title: circuit.title || '',
        acronym: circuit.acronym || '',
        organizer: circuit.organizer || '',
        logoUrl: circuit.logoUrl || '',
        color: circuit.color || 'bg-blue-600',
      });
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setLoadError(`${errTitle} : ${details}`);
    } finally {
      setIsLoading(false);
    }
  }, [circuitId]);

  useEffect(() => {
    if (isEditMode) {
      loadCircuit();
    }
  }, [isEditMode, loadCircuit]);

  // Vérification admin - doit être après les hooks
  if (userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-6">Vous devez être administrateur pour accéder à cette page.</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre est requis';
    }

    if (!formData.acronym.trim()) {
      newErrors.acronym = "L'acronyme est requis";
    } else if (formData.acronym.trim().length > 10) {
      newErrors.acronym = "L'acronyme doit faire au maximum 10 caractères";
    }

    if (!formData.organizer.trim()) {
      newErrors.organizer = "L'organisateur est requis";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = { ...formData };

      if (isEditMode) {
        await circuitsAPI.update(circuitId, payload);
      } else {
        await circuitsAPI.create(payload);
      }

      navigate('/admin/circuits');
    } catch (err) {
      const { error: errTitle, details } = handleApiError(err);
      setErrors((prev) => ({ ...prev, submit: `${errTitle} : ${details}` }));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center text-gray-500">
          Chargement du circuit...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin/circuits')}
            className="flex items-center text-gray-600 hover:text-blue-600 mb-6 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour à la gestion des circuits
          </button>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
              <h1 className="text-3xl font-bold flex items-center">
                <Layers className="mr-3" size={32} />
                {isEditMode ? 'Modifier le Circuit' : 'Créer un Nouveau Circuit'}
              </h1>
              <p className="mt-2 text-blue-100">
                {isEditMode ? 'Modifiez les informations de ce circuit de tournois' : 'Remplissez les informations pour ajouter un circuit'}
              </p>
            </div>
          </div>

          {loadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {loadError}
            </div>
          )}

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-6">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
            <div className="space-y-6">
              {/* Titre */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <Layers className="mr-2 text-blue-600" size={18} />
                  Titre *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex: World Poker Tour"
                  className={`w-full px-4 py-3 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Acronyme / Organisateur */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Acronyme *</label>
                  <input
                    type="text"
                    name="acronym"
                    value={formData.acronym}
                    onChange={handleChange}
                    placeholder="Ex: WPT"
                    maxLength={10}
                    className={`w-full px-4 py-3 border ${errors.acronym ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.acronym && <p className="text-red-500 text-sm mt-1">{errors.acronym}</p>}
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Organisateur *</label>
                  <input
                    type="text"
                    name="organizer"
                    value={formData.organizer}
                    onChange={handleChange}
                    placeholder="Ex: World Poker Tour Enterprises"
                    className={`w-full px-4 py-3 border ${errors.organizer ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.organizer && <p className="text-red-500 text-sm mt-1">{errors.organizer}</p>}
                </div>
              </div>

              {/* Couleur */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <Palette className="mr-2 text-blue-600" size={18} />
                  Couleur (classe Tailwind)
                </label>
                <div className="flex items-center gap-3">
                  <span className={`inline-block w-10 h-10 rounded-lg border border-gray-300 ${formData.color || 'bg-blue-600'}`} />
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="Ex: bg-blue-600"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Utilisée comme couleur de fond pour ce circuit sur les festivals liés (ex: bg-blue-600, bg-purple-600, bg-red-600...)
                </p>
              </div>

              {/* Logo URL */}
              <div>
                <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
                  <ImageIcon className="mr-2 text-blue-600" size={18} />
                  URL du logo (optionnel)
                </label>
                <input
                  type="url"
                  name="logoUrl"
                  value={formData.logoUrl}
                  onChange={handleChange}
                  placeholder="https://exemple.com/logo.png"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {formData.logoUrl && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-2">Aperçu :</p>
                    <img
                      src={formData.logoUrl}
                      alt="Aperçu"
                      className="w-32 h-32 object-contain border border-gray-300 rounded p-1"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Boutons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/admin/circuits')}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  disabled={isSaving}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center disabled:opacity-50"
                >
                  <Save className="mr-2 flex-shrink-0" size={20} />
                  {isSaving ? 'Enregistrement...' : isEditMode ? (
                    <>
                      <span className="sm:hidden">Enregistrer</span>
                      <span className="hidden sm:inline">Enregistrer les modifications</span>
                    </>
                  ) : 'Créer le circuit'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateEditCircuitPage;
